import type { MenuType, Role, MenuAccess, MenuFlag } from '@/shared/types';
const isAllowedByAccess = (
  access: MenuAccess | undefined,
  roleSet: Set<Role>,
) => {
  if (!access) return true;

  if (access.noneOf?.some((r) => roleSet.has(r))) return false;
  if (access.allOf?.length && !access.allOf.every((r) => roleSet.has(r)))
    return false;
  if (access.anyOf?.length && !access.anyOf.some((r) => roleSet.has(r)))
    return false;

  return true;
};

export const filterMenuByRoles = (
  items: MenuType[],
  userRoles: Role[],
  flags: ReadonlySet<MenuFlag> = new Set(),
): MenuType[] => {
  const roleSet = new Set(userRoles);

  const walk = (list: MenuType[]): MenuType[] =>
    list
      .filter((item) => item.display !== false)
      .filter((item) => !item.showIf || flags.has(item.showIf))
      .flatMap((item) => {
        const filteredChildren = item.children
          ? walk(item.children)
          : undefined;

        const selfAllowed = isAllowedByAccess(item.access, roleSet);
        const hasVisibleChildren = (filteredChildren?.length ?? 0) > 0;

        // If neither the item nor any child is allowed => remove it
        if (!selfAllowed && !hasVisibleChildren) return [];

        const next: MenuType = {
          ...item,
          link: selfAllowed ? item.link : undefined,
          children: filteredChildren,
        };

        return [next];
      });

  return walk(items);
};

export const collectParentKeys = (items: MenuType[]) => {
  const keys: string[] = [];
  const walk = (list: MenuType[]) => {
    for (const item of list) {
      if (item.children?.length) {
        keys.push(item.key);
        walk(item.children);
      }
    }
  };
  walk(items);
  return keys;
};
